import { Suspense, useEffect, type ReactNode } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import Toast from '@space-core/components/toast'
import { Actions, Entities } from '@space-core/datahub'

import { useShowGroups } from '@space-shell/common/components/baseLayout/hooks/useShowGroups'
import Content from '@space-shell/common/components/baseStructure/content'
import { SessionMode } from '@space-shell/common/components/baseStructure/enum'
import Header from '@space-shell/common/components/baseStructure/header'
import Menu from '@space-shell/common/components/baseStructure/menu'
import type { MenuItemProps } from '@space-shell/common/components/baseStructure/menu/menuItem/types'
import Structure from '@space-shell/common/components/baseStructure/structure'

import { ExternalLinksMenu } from '@space-shell/common/components/customMenu/externalLinksMenu'
import { MenuCommunityItem } from '@space-shell/common/components/customMenu/menuCommunityItem'
import { MainHeaderBar } from '@space-shell/common/components/headerBar/mainHeaderBar'
import { ViewAsBar } from '@space-shell/common/components/headerBar/viewAsBar'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'

import useCustomAssets from '@space-shell/common/hooks/useCustomAssets'
import useCustomColors from '@space-shell/common/hooks/useCustomColors'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import useGamificationToggle from '@space-shell/common/hooks/useGamificationToggle'
import { useGoogleAnalytics } from '@space-shell/common/hooks/useGoogleAnalytics'
import { useOptionalShowcase } from '@space-shell/common/hooks/useOptionalShowcase'
import useThemeContext from '@space-shell/common/hooks/useThemeContext'
import type { Membership } from '@space-shell/common/services/membership'

import { LanguageUtils } from '@space-shell/common/utils/language'
import { useMembershipStore, useViewAsStore } from '@space-shell/core/store'
import { lazyRemote } from '@space-shell/lib/runtime/lazyRemote'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { MenuProvider } from './context/useMenuContext'

import { useCanRenderPwaBanner } from './hooks/useCanRenderPwaBanner'

import { useContentConsumption } from './hooks/useContentConsumption'
import { useMenuItems } from './hooks/useMenuItems'

import { FAQ } from '../customMenu/faq'
import { PwaBar } from '../pwaBar'

const CommunityListMenu = lazyRemote('community/communityListMenu')

type BaseStructureProps = {
  children: ReactNode
}

const BaseStructure = ({ children }: BaseStructureProps) => {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { theme } = useThemeContext()
  const { authStatus } = useAuthContext()
  const { customColors } = useCustomColors()
  const { customAssets } = useCustomAssets()
  const { isEnabled: isViewAsEnabled } = useViewAsStore()
  const { membershipSlug, membershipData } = useMembershipStore()
  const shouldRenderGroups = useShowGroups(membershipData?.ownerId)
  const { sendDataHubEvent } = useDataHubEvents()
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()
  const { data: isGamificationEnabled } = useGamificationToggle()
  const isClassRoom = useContentConsumption()
  const { showcaseEnabled } = useOptionalShowcase()
  const { canRenderPwaBanner } = useCanRenderPwaBanner()
  const [moderationSupportWebEnableMenuItemFAQ] = useDecision(
    'moderation_support_web_enable_menu_item_faq'
  )
  const [{ enabled: enableNewCommunityMenu }] = useDecision('com_eng_web_enable_new_main_menu')

  useEffect(() => {
    if (theme) {
      customAssets(theme)
    }
  }, [customAssets, theme])

  useEffect(() => {
    if (Object.keys(membershipData as Membership).length !== 0 && theme) {
      customColors(String(membershipData?.settings.primaryColor), theme, SessionMode.DEFAULT)
    }
  }, [customColors, membershipData, theme])

  function handleClickInLogo() {
    const normalizedLocale = LanguageUtils.normalizeLocale(i18n.language)
    const homePath = `/${normalizedLocale}/club/${membershipSlug}`

    if (!showcaseEnabled) {
      window.location.replace(homePath)

      return
    }

    navigate(homePath)
  }

  function sendMenuTracker() {
    sendDataHubEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_MENU
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_MENU
    })
  }

  const customLogoUrl =
    membershipData?.settings?.logos.find((logo) => logo.type === theme)?.imageUrl ?? ''

  const menuItems = useMenuItems({
    t,
    navigate,
    language: i18n.language,
    sendDataHubEvent,
    slug: membershipSlug,
    sendGoogleAnalyticsEvent,
    pathname: location.pathname,
    isGamificationEnabled
  })

  return (
    <>
      {isViewAsEnabled && <ViewAsBar />}
      {canRenderPwaBanner && <PwaBar />}

      <MenuProvider>
        <Structure isViewAsEnabled={isViewAsEnabled}>
          <Header
            theme={theme}
            session={SessionMode.DEFAULT}
            customLogoUrl={customLogoUrl}
            handleClickInLogo={handleClickInLogo}
            membershipName={membershipData?.name ?? ''}
            logoAltText={membershipData?.settings?.logoAltText}
            isLoading={authStatus === AuthStatus.AUTHENTICATING}
            isClassRoom={isClassRoom}>
            <MainHeaderBar />
          </Header>

          <Menu
            theme={theme}
            onClick={sendMenuTracker}
            session={SessionMode.DEFAULT}
            menuItems={menuItems.filter((menuItem) => menuItem?.visible) as Array<MenuItemProps>}
            color={membershipData?.settings?.primaryColor}>
            {enableNewCommunityMenu && (
              <Suspense fallback={null}>
                <CommunityListMenu />
              </Suspense>
            )}

            {authStatus !== AuthStatus.AUTHENTICATING &&
              shouldRenderGroups &&
              !enableNewCommunityMenu && <MenuCommunityItem />}

            <ExternalLinksMenu />
            {moderationSupportWebEnableMenuItemFAQ.enabled ? <FAQ /> : null}
          </Menu>

          <Toast />

          <Content
            id='base-structure'
            className={clsx({ '_pt-12': !isClassRoom, '_pt-10': isClassRoom })}>
            {children}
          </Content>
        </Structure>
      </MenuProvider>
    </>
  )
}

export default BaseStructure
